import React, { useEffect, useState } from "react";
import MultiSelect from "../../../../components/MultiSelect";
import AddFeatureModal from "../../../../components/modals/AddFeatureModal";
import RBAC from "../../../../api/RBAC";
import AddRoleModal from "../../../../components/modals/AddRoleModal";
import EditRoleModal from "../../../../components/modals/EditRoleModal";
import { useDispatch, useSelector } from "react-redux";
import {
  refreshFeature,
  refreshRole,
  setRefreshFeature,
} from "../../../../store/authSlice";
import { toast } from "react-hot-toast";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const RBACRolesTab = () => {
  const dispatch = useDispatch();
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [loadingFeature, setLoadingFeature] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [featuresList, setFeaturesList] = useState([]);
  const refreshFeatureList = useSelector(refreshFeature);
  const refreshRolesList = useSelector(refreshRole);
  const [expandedRows, setExpandedRows] = useState({});
  const [permissionsArray, setPermissionsArray] = useState([]);
  const [selectedAccess, setSelectedAccess] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [editPermission, setEditPermission] = useState(null);
  const [selectEditRole, setSelectEditRole] = useState(null);
  const [existingRoles, setExistingRoles] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [isDeletingRole, setIsDeletingRole] = useState(false);

  const loadFeatures = async () => {
    setLoadingFeature(true);
    await RBAC.getAll().then((response) => {
      const organizedData = {};

      response.data.forEach((item) => {
        const { feature, ...rest } = item;
        if (!organizedData[feature]) {
          organizedData[feature] = [];
        }
        organizedData[feature].push(rest);
      });

      setFeaturesList(organizedData);
      setLoadingFeature(false);
    });
  };

  const loadRolePermissions = async (id, rolesData = null) => {
    setLoadingFeature(true);
    try {
      // Use provided rolesData or fall back to rolesList state
      const rolesToSearch = rolesData || rolesList;
      const foundRole = rolesToSearch.find((role) => role.id === id);

      if (foundRole) {
        const initialPermissions = [];
        Object.values(featuresList).forEach((records) => {
          records.forEach((record) => {
            initialPermissions.push({ feature_list_id: record.id, allow: 0 });
          });
        });
        
        // Handle data field - it might be null, a string, or already parsed
        let permissionsData = [];
        if (foundRole.data) {
          try {
            // Data might be a double-encoded JSON string, so parse twice if needed
            const parsedData = typeof foundRole.data === 'string' 
              ? JSON.parse(foundRole.data) 
              : foundRole.data;
            
            // Handle case where it's still a string after first parse
            permissionsData = typeof parsedData === 'string' 
              ? JSON.parse(parsedData) 
              : parsedData;
          } catch (e) {
            console.error("Error parsing role data:", e);
            permissionsData = [];
          }
        }
        
        const updatedPermissions = initialPermissions.map((permission) => {
          const existingPermission = permissionsData.find(
            (p) => p.feature_id === permission.feature_list_id && p.allow === 1
          );
          return existingPermission ? { ...permission, allow: 1 } : permission;
        });
        setPermissionsArray(updatedPermissions);
      }

      setLoadingFeature(false);
    } catch (error) {
      console.error("Error loading permissions:", error);
      setLoadingFeature(false);
    }
  };

  useEffect(() => {
    loadFeatures();
    dispatch(setRefreshFeature(false));
  }, [refreshFeatureList]);

  useEffect(() => {
    if (featuresList && selectEditRole) {
      loadRolePermissions(selectEditRole?.value);
    }
  }, [featuresList, selectEditRole]);

  useEffect(() => {
    const initialExpandedRows = {};
    Object.keys(featuresList).forEach((key, index) => {
      initialExpandedRows[index] = true;
    });
    setExpandedRows(initialExpandedRows);
  }, [featuresList]);

  const handleEditRoleChange = (selected) => {
    if (selected) {
      // Find the full role data including category_type
      const fullRoleData = existingRoles.find((role) => role.role_id === selected.role_id);
      setSelectEditRole(fullRoleData || selected);
    } else {
      setSelectEditRole(null);
      setPermissionsArray([]);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectEditRole) return;
    
    setIsDeletingRole(true);
    try {
      const roleId = selectEditRole.role_id;
      const response = await RBAC.deleteRole(roleId);
      if (response) {
        toast.success(`Role "${selectEditRole.label}" deleted successfully`);
        await loadExistingRoles();
        setShowDeleteModal(false);
        setSelectEditRole(null);
        setPermissionsArray([]);
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role. Please try again.");
    } finally {
      setIsDeletingRole(false);
    }
  };

  const handleRowClick = (index) => {
    setExpandedRows((prevExpandedRows) => ({
      ...prevExpandedRows,
      [index]: !prevExpandedRows[index],
    }));
  };

  const handleCheckClick = (id, checked, records) => {
    if (records) {
      const updatedPermissionsArray = permissionsArray.map((permission) => {
        const recordExists = records.some(
          (record) => record.id === permission.feature_list_id
        );
        return {
          ...permission,
          allow: checked
            ? recordExists
              ? 0
              : permission.allow
            : recordExists
              ? 1
              : permission.allow,
        };
      });
      setSelectedAccess(updatedPermissionsArray);
      setPermissionsArray(updatedPermissionsArray);
    } else {
      const updatedPermission = permissionsArray.find(
        (permission) => permission.feature_list_id === id
      );
      if (updatedPermission) {
        const updatedPermissionsArray = permissionsArray.map((permission) => {
          if (permission.feature_list_id === id) {
            return {
              ...permission,
              allow: checked ? 0 : 1,
            };
          }
          return permission;
        });
        setSelectedAccess(updatedPermissionsArray);
        setPermissionsArray(updatedPermissionsArray);
      }
    }
  };

  const handleEditRole = async () => {
    setLoadingPermissions(true);
    
    // Get the role_id from the selected role
    const selectedRoleData = existingRoles.find((role) => role.value === selectEditRole?.value);
    const roleId = selectedRoleData?.role_id || selectEditRole?.value;
    
    // Format permissions array for data field
    const permissionsData = permissionsArray.map((access) => ({
      feature_id: access.feature_list_id,
      allow: access.allow,
    }));
    
    // Stringify both roles and data fields to match the API format
    const payload = {
      role_id: String(roleId),
      permissions: permissionsData,
    };

    try {
      // Use id field for the endpoint
      const response = await RBAC.getRoleById(selectEditRole?.value, payload);
      if (response) {
        await refreshRoles();
        toast.success("Role Edited Successfully");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role. Please try again.");
    }

    setLoadingPermissions(false);
  };

  const loadExistingRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const response = await RBAC.getRolesList();
      const rolesDataArray = response.data;
      setRolesList(rolesDataArray);
      let rolesData = [];

      for (let i = 0; i < rolesDataArray.length; i++) {
        const el = rolesDataArray[i];
        // Only include roles that have an id (can be updated)
        if (el.id !== null) {
          const categoryType = el.category_type || "default";
          rolesData.push({
            label: el.role_name || el.name,
            value: el.id,
            role_id: el.role_id,
            data: el.data,
            category_type: categoryType,
          });
        }
      }
      setExistingRoles(rolesData);
      
      // Return the roles data array so it can be used immediately
      return rolesDataArray;
    } catch (error) {
      console.error("Error getting roles:", error);
      return null;
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const refreshRoles = async () => {
    const updatedRolesList = await loadExistingRoles();
    if (updatedRolesList && selectEditRole) {
      // Pass the fresh roles data directly to loadRolePermissions
      await loadRolePermissions(selectEditRole?.value, updatedRolesList);
    }
  };

  useEffect(() => {
    refreshRoles();
  }, [refreshRolesList]);

  const handleEditFeature = async (item, permission) => {
    const editedPermission = { ...permission, item };
    setEditPermission(editedPermission);
    setShowModal(true);
  };

  return (
    <>
      {showModal && (
        <AddFeatureModal
          open={showModal}
          setOpen={setShowModal}
          editPermission={editPermission}
          setEditPermission={setEditPermission}
        />
      )}

      {showRoleModal && (
        <AddRoleModal
          open={showRoleModal}
          setOpen={setShowRoleModal}
          featuresList={featuresList}
        />
      )}

      {showEditRoleModal && selectEditRole && (
        <EditRoleModal
          open={showEditRoleModal}
          setOpen={setShowEditRoleModal}
          role={selectEditRole}
          onSuccess={async () => {
            await loadExistingRoles();
            setSelectEditRole(null);
            setPermissionsArray([]);
          }}
        />
      )}

      {showDeleteModal && selectEditRole && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setShowDeleteModal(false);
              }}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Role</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isDeletingRole}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-700 mb-2">
                  Are you sure you want to delete this role?
                </p>
                <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                  <p className="text-sm font-medium text-gray-900">
                    {selectEditRole.label}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                  }}
                  disabled={isDeletingRole}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteRole}
                  disabled={isDeletingRole}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeletingRole ? (
                    <>
                      <i className="fad fa-circle-notch fa-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Add New Feature Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Manage Features</h2>
              <p className="text-sm text-gray-600 mt-1">Create and manage system features</p>
            </div>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setShowModal(true)}
            >
              <i className="fas fa-plus mr-2"></i>
              Add New Feature
            </button>
          </div>
        </div>

        {/* Edit Roles Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Edit Roles</h2>
            <p className="text-sm text-gray-600 mt-1">Modify role permissions and create new roles</p>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Role to Edit
                </label>
                <div className="border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
                  <MultiSelect
                    key="roles"
                    options={existingRoles}
                    onChange={handleEditRoleChange}
                    value={selectEditRole}
                    menuPlacement={"bottom"}
                    isLoading={isLoadingRoles}
                    formatOptionLabel={({ label, category_type }) => (
                      <div className="flex items-center justify-between">
                        <span>{label}</span>
                        {category_type === "default" && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  className="w-full md:w-auto inline-flex items-center justify-center px-6 py-2.5 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                  onClick={() => setShowRoleModal(true)}
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add New Role
                </button>
              </div>
            </div>

            {selectEditRole && selectEditRole.category_type === "custom" && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-lg shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={loadingPermissions}
                >
                  <i className="fas fa-trash mr-2"></i>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Features List Table Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6" data-permissions-section>
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectEditRole ? (
                <>
                  Role Permissions: <span className="text-primary-600">{selectEditRole?.label}</span>
                </>
              ) : (
                "Features List"
              )}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {selectEditRole
                ? "Manage permissions for this role"
                : "View all system features and permissions. Select a role to edit permissions."}
            </p>
          </div>

          {loadingFeature ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-sm text-gray-600">Loading features...</p>
            </div>
          ) : Object.keys(featuresList).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">No features found. Add a new feature to get started.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50/50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                        Features
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                        Permission
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                        Access
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(featuresList).map(([item, records], index) => (
                      <React.Fragment key={index}>
                        <tr className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() => handleRowClick(index)}
                                className="mr-3 text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                <i
                                  className={`fas ${expandedRows[index]
                                      ? "fa-chevron-up"
                                      : "fa-chevron-down"
                                    } text-sm`}
                                ></i>
                              </button>
                              <i className="fas fa-bars text-primary-500 mr-2 text-sm"></i>
                              <span className="text-sm font-semibold text-gray-900">{item}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">{item}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <label className={`inline-flex items-center ${selectEditRole ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                              <input
                                type="checkbox"
                                disabled={!selectEditRole}
                                className={`w-4 h-4 text-primary-600 border-gray-500 rounded focus:ring-primary-500 focus:ring-2 ${selectEditRole
                                    ? 'cursor-pointer'
                                    : 'cursor-not-allowed opacity-50'
                                  }`}
                                checked={selectEditRole ? records.every((record) =>
                                  permissionsArray.some(
                                    (permission) =>
                                      permission.feature_list_id === record.id &&
                                      permission.allow === 1
                                  )
                                ) : false}
                                onChange={() =>
                                  selectEditRole &&
                                  handleCheckClick(
                                    index,
                                    records.every((record) =>
                                      permissionsArray.some(
                                        (permission) =>
                                          permission?.feature_list_id === record.id &&
                                          permission.allow === 1
                                      )
                                    ),
                                    records
                                  )
                                }
                              />
                            </label>
                          </td>
                        </tr>
                        {expandedRows[index] &&
                          records &&
                          records.length > 0 &&
                          records.map((permission, permissionIndex) => (
                            <tr
                              key={`${index}_expanded_${permissionIndex}`}
                              className="hover:bg-gray-50 transition-colors duration-150 bg-gray-50/30"
                            >
                              <td className="px-6 py-3 pl-12">
                                <div className="flex items-center">
                                  <i className="fas fa-circle text-[6px] mr-2 text-primary-500"></i>
                                  <span className="text-sm text-gray-700">{permission.operations}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleEditFeature(item, permission)}
                                    className="ml-3 text-primary-600 hover:text-primary-700 transition-colors"
                                    title="Edit permission"
                                  >
                                    <i className="fad fa-pencil-alt text-xs"></i>
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-3">
                                <span className="text-sm text-gray-600">
                                  {item + "." + permission.operations}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-center">
                                <label className={`inline-flex items-center ${selectEditRole ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                  <input
                                    type="checkbox"
                                    disabled={!selectEditRole}
                                    className={`w-4 h-4 text-primary-600 border-gray-500 rounded focus:ring-primary-500 focus:ring-2 ${selectEditRole
                                        ? 'cursor-pointer'
                                        : 'cursor-not-allowed opacity-50'
                                      }`}
                                    checked={
                                      selectEditRole && permissionsArray?.find(
                                        (item) =>
                                          item.feature_list_id === permission.id
                                      )?.allow === 1
                                    }
                                    onChange={() =>
                                      selectEditRole &&
                                      handleCheckClick(
                                        permission?.id,
                                        permissionsArray?.find(
                                          (item) =>
                                            item.feature_list_id === permission.id
                                        )?.allow === 1
                                      )
                                    }
                                  />
                                </label>
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectEditRole && (
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex items-center px-6 py-2.5 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleEditRole()}
                    disabled={!selectEditRole || loadingPermissions}
                  >
                    {loadingPermissions ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RBACRolesTab;
